import { Component, OnInit, OnDestroy } from "@angular/core";
import { FormControl, FormGroup } from "@angular/forms";
import { MovimientoService } from "app/services/movimiento.service";
import { SpinnerService } from "app/services/spinner.service";
import { Subscription, of } from "rxjs";
import { catchError, map } from "rxjs/operators";
import * as moment from "moment";
import { saveAs } from "file-saver";

const SUB_TIPO_LABELS: Record<string, string> = {
  reserva: "Reserva",
  saldo: "Saldo",
  unico: "Único",
};

const ESTADO_LABELS: Record<string, string> = {
  pendiente: "Pendiente",
  pagado: "Pagado",
  fallido: "Fallido",
  reembolsado: "Reembolsado",
  anulado: "Anulado",
};

@Component({
  selector: "app-informe-movimientos",
  templateUrl: "./informe-movimientos.component.html",
  styleUrls: ["./informe-movimientos.component.scss"],
})
export class InformeMovimientosComponent implements OnInit, OnDestroy {
  movimientos: any[] = [];
  medicos: any[] = [];
  pacientes: any[] = [];
  totalCobrado: number = 0;
  totalGastado: number = 0;
  saldo: number = 0;
  totalPendienteCobrar: number = 0;

  displayedColumns: string[] = [
    "fecha_hora",
    "fecha_turno",
    "medico",
    "paciente",
    "tipo",
    "sub_tipo",
    "estado",
    "monto_bruto",
    "comision_medico",
    "descripcion",
  ];

  filtrosForm = new FormGroup({
    fechaDesde: new FormControl(null),
    fechaHasta: new FormControl(null),
    tipo: new FormControl(""),
    idMedico: new FormControl(""),
    idPaciente: new FormControl(""),
    estado: new FormControl(""),
  });

  private subscripciones = new Subscription();

  constructor(
    private movimientoService: MovimientoService,
    private spinnerService: SpinnerService,
  ) {}

  ngOnInit() {
    this.spinnerService.mostrarSpinner();

    this.subscripciones.add(
      this.movimientoService.listarTodos().pipe(
        map((data) => {
          this.setData(data);
          this.spinnerService.ocultarSpinner();
        }),
        catchError(() => {
          this.spinnerService.ocultarSpinner();
          return of(null);
        }),
      ).subscribe()
    );
  }

  ngOnDestroy() {
    this.subscripciones.unsubscribe();
  }

  filtrar() {
    this.spinnerService.mostrarSpinner();
    const filtros: any = {};
    if (this.filtrosForm.value.fechaDesde) {
      filtros.fechaDesde = moment(this.filtrosForm.value.fechaDesde).format("YYYY-MM-DD");
    }
    if (this.filtrosForm.value.fechaHasta) {
      filtros.fechaHasta = moment(this.filtrosForm.value.fechaHasta).format("YYYY-MM-DD");
    }
    if (this.filtrosForm.value.tipo) {
      filtros.tipo = this.filtrosForm.value.tipo;
    }
    if (this.filtrosForm.value.idMedico) {
      filtros.idMedico = this.filtrosForm.value.idMedico;
    }
    if (this.filtrosForm.value.idPaciente) {
      filtros.idPaciente = this.filtrosForm.value.idPaciente;
    }
    if (this.filtrosForm.value.estado) {
      filtros.estado = this.filtrosForm.value.estado;
    }

    this.subscripciones.add(
      this.movimientoService.listarTodos(filtros).subscribe(
        (data) => {
          this.setData(data);
          this.spinnerService.ocultarSpinner();
        },
        () => this.spinnerService.ocultarSpinner(),
      )
    );
  }

  limpiarFiltros() {
    this.filtrosForm.reset({
      fechaDesde: null,
      fechaHasta: null,
      tipo: "",
      idMedico: "",
      idPaciente: "",
      estado: "",
    });
    this.filtrar();
  }

  exportarExcel() {
    import("xlsx").then((XLSX) => {
      const data = this.movimientos.map((m) => ({
        Fecha: moment(m.fecha_hora).format("DD/MM/YYYY HH:mm"),
        "Fecha turno": m.fecha_turno ? moment(m.fecha_turno).format("DD/MM/YYYY HH:mm") : "",
        Médico: m.medico ? (m.medico.name + " " + m.medico.surname) : "",
        Paciente: m.paciente ? (m.paciente.name + " " + m.paciente.surname) : "",
        Tipo: m.tipo === "gasto" ? "Gasto" : "Turno",
        "Sub-tipo": this.labelSubTipo(m.sub_tipo),
        Estado: this.labelEstado(m.estado),
        "Monto bruto": m.monto_bruto ?? m.monto,
        "Comisión médico": m.split === false ? "—" : (m.comision_medico ?? ""),
        Descripción: m.descripcion || "",
      }));

      const fila = (tipoLabel: string, monto: number | null) => ({
        Fecha: "",
        "Fecha turno": "",
        Médico: "",
        Paciente: "",
        Tipo: tipoLabel,
        "Sub-tipo": "",
        Estado: "",
        "Monto bruto": monto,
        "Comisión médico": "",
        Descripción: "",
      });

      data.push(
        fila("", null),
        fila("Cobrado", this.totalCobrado),
        fila("Pendiente", this.totalPendienteCobrar),
        fila("Gastado", this.totalGastado),
        fila("Saldo", this.saldo),
      );

      const ws = XLSX.utils.json_to_sheet(data);
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, "Movimientos");
      const buffer = XLSX.write(wb, { bookType: "xlsx", type: "array" });
      const blob = new Blob([buffer], { type: "application/octet-stream" });
      saveAs(blob, `informe_movimientos.xlsx`);
    });
  }

  getNombrePaciente(element: any): string {
    if (element.paciente) {
      return element.paciente.name + " " + element.paciente.surname;
    }
    return "";
  }

  getNombreMedico(element: any): string {
    if (element.medico) {
      return element.medico.name + " " + element.medico.surname;
    }
    return "";
  }

  labelSubTipo(subTipo: string | null): string {
    if (!subTipo) return "";
    return SUB_TIPO_LABELS[subTipo] ?? subTipo;
  }

  labelEstado(estado: string | null): string {
    if (!estado) return "";
    return ESTADO_LABELS[estado] ?? estado;
  }

  private setData(data: any) {
    this.movimientos = data.listaMovimientos;
    this.totalCobrado = data.totalCobrado;
    this.totalGastado = data.totalGastado;
    this.saldo = data.saldo;
    this.totalPendienteCobrar = data.totalPendienteCobrar ?? 0;
    this.medicos = this.extraerUnicos(this.movimientos, "medico");
    this.pacientes = this.extraerUnicos(this.movimientos, "paciente");
  }

  private extraerUnicos(movimientos: any[], campo: "medico" | "paciente"): any[] {
    const map = new Map<number, any>();
    for (const m of movimientos ?? []) {
      const u = m[campo];
      if (u?.id && !map.has(u.id)) {
        map.set(u.id, u);
      }
    }
    return Array.from(map.values()).sort((a, b) =>
      (a.name + " " + a.surname).localeCompare(b.name + " " + b.surname)
    );
  }
}
